import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/use-auth";
import { UserSystemsMenu } from "@/components/UserSystemsMenu";
import { LoginDropdown } from "@/components/LoginDropdown";
import { ForgotPasswordModal } from "@/components/ForgotPasswordModal";
import { PortalSubscriptionModal } from "@/components/PortalSubscriptionModal";
import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { DefaultSystemModal } from "@/components/DefaultSystemModal";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { User, LogOut, Settings } from "lucide-react";
import { ThemeToggle } from "@/components/theme-toggle";
import portalOneLogo from "@/assets/portalone-suite-logo.png";

const publicNavigation = [
  { name: "Home", to: "/", isAnchor: false },
  { name: "Soluções", to: "#solutions", isAnchor: true },
  { name: "Preços", to: "#pricing", isAnchor: true },
  { name: "Contato", to: "/contact", isAnchor: false },
];

const scrollToSection = (elementId: string) => {
  const element = document.getElementById(elementId);
  if (element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};

export function Navigation() {
  const { user, profile, signOut } = useAuth();
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);

  const handleSignOut = async () => {
    await signOut();
  };

  const userInitials = profile?.full_name
    ? profile.full_name
        .split(' ')
        .map((n: string) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : user?.email?.[0]?.toUpperCase() || 'U';

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-16 max-w-screen-2xl items-center">
        <div className="mr-4 hidden md:flex">
        <Link to="/" className="mr-6 flex items-center space-x-2">
          <img 
            src={portalOneLogo} 
            alt="PortalONE Suite" 
            className="h-8 w-auto object-contain" 
          />
        </Link>
          
          {/* Show public navigation when not authenticated */}
          {!user && (
            <nav className="flex items-center gap-6 text-sm">
              {publicNavigation.map((item) => (
                item.isAnchor ? (
                  <button
                    key={item.to}
                    onClick={() => scrollToSection(item.to.substring(1))}
                    className={cn(
                      "transition-colors hover:text-foreground/80",
                      "text-foreground/60"
                    )}
                  >
                    {item.name}
                  </button>
                ) : (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={cn(
                      "transition-colors hover:text-foreground/80",
                      "text-foreground/60"
                    )}
                  >
                    {item.name}
                  </Link>
                )
              ))}
            </nav>
          )}
        </div>
        
        <div className="flex flex-1 items-center justify-between space-x-2 md:justify-end">
          <div className="w-full flex-1 md:w-auto md:flex-none">
            {user ? (
              // Authenticated user menu
              <div className="flex items-center gap-4">
                {/* Systems menu */}
                <UserSystemsMenu />
                
                {/* User dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={profile?.avatar_url} alt={profile?.full_name} />
                        <AvatarFallback>{userInitials}</AvatarFallback>
                      </Avatar>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-56" align="end" forceMount>
                    <DropdownMenuLabel className="font-normal">
                      <div className="flex flex-col space-y-1">
                        <p className="text-sm font-medium leading-none">
                          {profile?.full_name || 'Usuário'}
                        </p>
                        <p className="text-xs leading-none text-muted-foreground">
                          {user.email}
                        </p>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    
                    <DropdownMenuItem asChild>
                      <DefaultSystemModal 
                        trigger={
                          <Button variant="ghost" size="sm" className="w-full justify-start">
                            <Settings className="mr-2 h-4 w-4" />
                            Configurações
                          </Button>
                        }
                      />
                    </DropdownMenuItem>
                    
                    <DropdownMenuItem asChild>
                      <div className="flex items-center justify-between w-full px-2 py-1">
                        <span className="text-sm">Tema</span>
                        <ThemeToggle />
                      </div>
                    </DropdownMenuItem>
                    
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleSignOut}>
                      <LogOut className="mr-2 h-4 w-4" />
                      <span>Sair</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ) : (
              // Unauthenticated user buttons
              <div className="flex items-center gap-2">
                <ThemeToggle />
                <LoginDropdown 
                  onForgotPassword={() => setShowForgotPassword(true)}
                  onSignup={() => setShowSubscriptionModal(true)}
                />
                <Button onClick={() => setShowSubscriptionModal(true)}>
                  Começar Agora
                </Button>
                
                <ForgotPasswordModal 
                  open={showForgotPassword} 
                  onOpenChange={setShowForgotPassword}
                />
                
                <PortalSubscriptionModal 
                  open={showSubscriptionModal} 
                  onOpenChange={setShowSubscriptionModal}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}